import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Paper, CircularProgress, Alert,
    InputAdornment, Grid, Divider
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { settingsAPI } from '../../services/apiService';

const GlobalPricingSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dirty, setDirty] = useState(false);

    const [settings, setSettings] = useState({
        minimumFare: 0,
        minStopCost: 0,
        stopCompanySharePercent: 0.30,
        stopPerMileRate: 3,
        formulaCompanySharePercent: 0.30,
        roundTripMultiplier: 1.5,
        defaultPerMileRate: 0,
        defaultMinPerHourRate: 30,
    });

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const companyData = await settingsAPI.get();
            setSettings({
                minimumFare: companyData.minimumFare ?? 70,
                minStopCost: companyData.minStopCost ?? 10,
                stopCompanySharePercent: companyData.stopCompanySharePercent ?? 0.30,
                stopPerMileRate: companyData.stopPerMileRate ?? 3,
                formulaCompanySharePercent: companyData.formulaCompanySharePercent ?? 0.30,
                roundTripMultiplier: companyData.roundTripMultiplier ?? 1.5,
                defaultPerMileRate: companyData.defaultPerMileRate ?? 2.00,
                defaultMinPerHourRate: companyData.defaultMinPerHourRate ?? 30,
            });
            setDirty(false);
            setError(null);
        } catch (err) {
            setError('Failed to load settings: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            // Partial save: the server merges only the fields present in this payload onto the
            // existing settings row, so sending just the pricing fields is safe and won't touch
            // (or clobber) any other settings.
            await settingsAPI.update({
                minimumFare: Number(settings.minimumFare),
                minStopCost: Number(settings.minStopCost),
                stopCompanySharePercent: Number(settings.stopCompanySharePercent),
                stopPerMileRate: Number(settings.stopPerMileRate),
                formulaCompanySharePercent: Number(settings.formulaCompanySharePercent),
                roundTripMultiplier: Number(settings.roundTripMultiplier),
                defaultPerMileRate: Number(settings.defaultPerMileRate),
                defaultMinPerHourRate: Number(settings.defaultMinPerHourRate),
            });
            setDirty(false);
            setSuccess('Global pricing settings saved');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Save failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    const SectionCard = ({ title, children }) => (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
                {children}
            </Box>
        </Paper>
    );

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            <Paper variant="outlined" sx={{ p: 1.5, mb: 3, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    <strong>One-way:</strong> perMileRate × distance (mi) + tolls &nbsp;·&nbsp;
                    <strong>Round trip:</strong> one-way × multiplier &nbsp;·&nbsp;
                    <strong>Stop:</strong> max(min stop cost, dist × rate) &nbsp;·&nbsp;
                    <strong>Rush hour:</strong> configured in Rush Hour Fees tab
                </Typography>
            </Paper>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Global Pricing Settings</Typography>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !dirty}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
                {/* ── General ── */}
                <Grid size={4}>
                    <SectionCard title="General">
                        <TextField
                            fullWidth label="Minimum Fare ($)" type="number"
                            value={settings.minimumFare}
                            onChange={e => handleChange('minimumFare', e.target.value)}
                            helperText="Floor price for formula-based rides"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                        <TextField
                            fullWidth label="Default Per Mile Rate ($)" type="number"
                            value={settings.defaultPerMileRate}
                            onChange={e => handleChange('defaultPerMileRate', e.target.value)}
                            helperText="Fallback when no car-type-specific rate exists"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                        <TextField
                            fullWidth label="Round Trip Multiplier" type="number"
                            value={settings.roundTripMultiplier}
                            onChange={e => handleChange('roundTripMultiplier', e.target.value)}
                            helperText="e.g. 1.5 = 150% of one-way"
                            inputProps={{ step: 0.1, min: 1 }}
                        />
                    </SectionCard>
                </Grid>

                {/* ── Stop Pricing ── */}
                <Grid size={4}>
                    <SectionCard title="Stop Pricing">
                        <TextField
                            fullWidth label="Min Stop Cost ($)" type="number"
                            value={settings.minStopCost}
                            onChange={e => handleChange('minStopCost', e.target.value)}
                            helperText="Minimum flat fee per stop"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                        <TextField
                            fullWidth label="Stop Per-Mile Rate ($/mi)" type="number"
                            value={settings.stopPerMileRate}
                            onChange={e => handleChange('stopPerMileRate', e.target.value)}
                            helperText="Charge = max(min stop cost, miles × rate)"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            inputProps={{ step: 0.5, min: 0 }}
                        />
                        <TextField
                            fullWidth label="Stop Company Share (%)" type="number"
                            value={settings.stopCompanySharePercent}
                            onChange={e => handleChange('stopCompanySharePercent', e.target.value)}
                            helperText="e.g. 0.30 = 30% to company, 70% to driver"
                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                        />
                    </SectionCard>
                </Grid>

                {/* ── Formula ── */}
                <Grid size={4}>
                    <SectionCard title="Formula">
                        <TextField
                            fullWidth label="Formula Company Share (%)" type="number"
                            value={settings.formulaCompanySharePercent}
                            onChange={e => handleChange('formulaCompanySharePercent', e.target.value)}
                            helperText="e.g. 0.30 = 30%"
                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                        />
                        <TextField
                            fullWidth label="Default Min Per-Hour Rate ($/hr)" type="number"
                            value={settings.defaultMinPerHourRate}
                            onChange={e => handleChange('defaultMinPerHourRate', e.target.value)}
                            helperText="Hourly floor for formula rides (0 = disabled)"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            inputProps={{ step: 1, min: 0 }}
                        />
                    </SectionCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GlobalPricingSettings;
